import { api } from '../console/api.ts';
import { empty, pageHead } from '../console/components.ts';
import { esc, icon } from '../console/format.ts';
import { type Actions, render } from '../console/router.ts';
import { has } from '../console/session.ts';
import { $, act } from '../console/ui.ts';
import { selectControl } from '../controls.ts';
import {
  loadPeople,
  newTaskFields,
  type Person,
  readNewTask,
  type TaskRow,
  taskItemHtml,
} from './seguimiento.ts';

/**
 * «Mis tareas» (#317, ADR-0043): the person's agenda — overdue, today, upcoming and the week's
 * calendar — on the business clock of Guatemala (the API decides what is overdue, never this
 * browser's clock). Management (direction, administration, the agency manager) switches to the
 * team's tasks, filters them by person and hands them to another; the board's indicator lives in
 * «Resumen». Every agency role sees it.
 */

type Tab = 'overdue' | 'today' | 'upcoming' | 'week';
type MyTasks = {
  scope: 'mine' | 'team';
  now: string;
  today: string;
  overdue: TaskRow[];
  dueToday: TaskRow[];
  upcoming: TaskRow[];
  week: { date: string; tasks: TaskRow[] }[];
  can: { team: boolean };
};

let tab: Tab | null = null;
let scope: 'mine' | 'team' = 'mine';
/** The team's tasks of one person (management), or everyone's. */
let who = '';
/** Any date of the week the calendar shows (null: this week). */
let week: string | null = null;

/** A new session opens its own tasks, on the first tab with something to do. */
export function resetMyTasks() {
  tab = null;
  scope = 'mine';
  who = '';
  week = null;
}

const TABS: [Tab, string][] = [
  ['overdue', 'Atrasadas'],
  ['today', 'Hoy'],
  ['upcoming', 'Próximas'],
  ['week', 'Semana'],
];

const WEEKDAY = new Intl.DateTimeFormat('es-GT', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const dayName = (d: string) => WEEKDAY.format(new Date(`${d}T12:00:00Z`)).replace(/\./g, '');
const shift = (d: string, days: number) => {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + days);
  return x.toISOString().slice(0, 10);
};

/** Management: the team's tasks, or one person's. */
const managesTeam = () => has('agency_director', 'agency_admin', 'agency_manager');

function kpis(d: MyTasks) {
  const tile = (key: Tab, iconName: string, label: string, n: number, foot: string, tone = '') =>
    `<button type="button" class="card kpi kpi-button ${tone}" data-mt-tab="${key}" aria-pressed="${tab === key}"><span class="kpi-label">${icon(iconName)}${esc(label)}</span><span class="kpi-value">${n}</span><span class="kpi-foot">${esc(foot)}</span></button>`;
  return `<section class="kpis task-kpis" aria-label="Resumen de las tareas" data-component="task-kpis">
    ${tile('overdue', 'alert', 'Atrasadas', d.overdue.length, d.overdue.length ? 'ya pasó su hora' : 'Nada atrasado', d.overdue.length ? 'bad' : '')}
    ${tile('today', 'clock', 'Hoy', d.dueToday.length, d.dueToday.length ? 'vencen más tarde hoy' : 'Nada más para hoy')}
    ${tile('upcoming', 'calendar', 'Próximas', d.upcoming.length, d.upcoming.length ? 'después de hoy' : 'Nada programado')}
  </section>`;
}

function list(rows: TaskRow[], choices: Person[], what: [string, string]) {
  return rows.length
    ? `<ul class="task-list" data-component="task-list">${rows.map((t) => taskItemHtml(t, { withAssignee: scope === 'team', choices })).join('')}</ul>`
    : empty('ok', what[0], what[1]);
}

function calendar(d: MyTasks, choices: Person[]) {
  const first = d.week[0]?.date ?? d.today;
  const nav = `<div class="toolbar week-nav" role="group" aria-label="Semana">
    <button type="button" class="btn sm" data-mt-week="${esc(shift(first, -7))}" aria-label="Semana anterior">‹ Anterior</button>
    <button type="button" class="btn sm" data-mt-week="" ${week ? '' : 'aria-pressed="true"'}>Esta semana</button>
    <button type="button" class="btn sm" data-mt-week="${esc(shift(first, 7))}" aria-label="Semana siguiente">Siguiente ›</button></div>`;
  const days = d.week
    .map(
      (
        day,
      ) => `<section class="week-day ${day.date === d.today ? 'today' : ''}" aria-label="${esc(dayName(day.date))}">
      <h3>${esc(dayName(day.date))}${day.date === d.today ? ' <span class="pill info">Hoy</span>' : ''}</h3>
      ${day.tasks.length ? `<ul class="task-list compact">${day.tasks.map((t) => taskItemHtml(t, { withAssignee: scope === 'team', choices })).join('')}</ul>` : '<p class="muted small">Sin tareas.</p>'}
    </section>`,
    )
    .join('');
  return `${nav}<div class="week-grid" data-component="week">${days}</div>`;
}

export async function misTareas() {
  const team = scope === 'team' && managesTeam();
  const q = new URLSearchParams();
  if (team) q.set('scope', 'team');
  if (team && who) q.set('assignee', who);
  if (week) q.set('week', week);
  const [d, choices] = await Promise.all([
    api<MyTasks>('GET', `/v1/tasks${q.toString() ? `?${q}` : ''}`),
    managesTeam() ? loadPeople() : Promise.resolve([] as Person[]),
  ]);
  // The first time, the tab with something to do: overdue first.
  if (!tab) tab = d.overdue.length ? 'overdue' : d.dueToday.length ? 'today' : 'upcoming';
  const scopeSwitch = d.can.team
    ? `<div class="lead-filters" role="group" aria-label="De quién">
        <button type="button" class="btn sm" data-mt-scope="mine" aria-pressed="${!team}">Mías</button>
        <button type="button" class="btn sm" data-mt-scope="team" aria-pressed="${team}">Del equipo</button></div>`
    : '';
  const head = pageHead(
    team ? 'Tareas del equipo' : 'Mis tareas',
    team
      ? 'Lo que cada persona de la agencia tiene que hacer: atrasadas, de hoy y próximas. Reasigna las que hagan falta.'
      : 'Lo que tienes que hacer: llamadas, visitas, trámites. Atrasadas, de hoy y próximas, y tu semana.',
    scopeSwitch,
  );
  const filter =
    team && choices.length
      ? `<div class="field mt-person">${selectControl({ id: 'mt-person', label: 'Persona' }, [
          { value: '', label: 'Todo el equipo', selected: !who },
          ...choices.map((p) => ({
            value: p.memberId,
            label: p.name,
            selected: p.memberId === who,
          })),
        ])}</div>`
      : '';
  const form = `<details class="card task-new" data-component="task-new"${d.overdue.length + d.dueToday.length + d.upcoming.length ? '' : ' open'}>
    <summary class="task-new-summary">${icon('calendar')}<span>Nueva tarea</span></summary>
    <div class="task-new-body">${newTaskFields('mt', d.today, managesTeam() ? choices : null)}
    <div class="order-buttons"><button type="button" class="btn primary" data-mt-new>Crear tarea</button></div></div></details>`;
  const tabs = TABS.map(([k, label]) => {
    const n =
      k === 'overdue'
        ? d.overdue.length
        : k === 'today'
          ? d.dueToday.length
          : k === 'upcoming'
            ? d.upcoming.length
            : null;
    return `<button type="button" class="btn sm" role="tab" id="mt-${k}" aria-selected="${tab === k}" aria-controls="mt-panel" data-mt-tab="${k}">${esc(label)}${n === null ? '' : ` <span class="tab-count">${n}</span>`}</button>`;
  }).join('');
  const body =
    tab === 'overdue'
      ? list(d.overdue, choices, [
          'Nada atrasado',
          'Todas las tareas están al día. Las que pasen su hora sin hacerse aparecen aquí.',
        ])
      : tab === 'today'
        ? list(d.dueToday, choices, [
            'Nada más para hoy',
            'No quedan tareas que venzan hoy. Crea una arriba si hace falta.',
          ])
        : tab === 'upcoming'
          ? list(d.upcoming, choices, ['Nada programado', 'No hay tareas para los próximos días.'])
          : calendar(d, choices);
  return `${head}${kpis(d)}${form}
    <div class="card toolbar task-toolbar"><div class="lead-filters" role="tablist" aria-label="Qué tareas ver">${tabs}</div><span class="spacer"></span>${filter}</div>
    <section class="card task-panel" id="mt-panel" role="tabpanel" aria-labelledby="mt-${tab}" data-component="my-tasks">${body}</section>`;
}

export const actions: Actions = {
  click: {
    mtTab: (_, d) => {
      const next = d.mtTab as Tab | undefined;
      if (!next) return;
      tab = next;
      void render('mis-tareas', { quiet: true }).then(() => $(`#mt-${next}`)?.focus());
    },
    mtScope: (_, d) => {
      scope = d.mtScope === 'team' ? 'team' : 'mine';
      who = '';
      void render('mis-tareas', { quiet: true });
    },
    mtWeek: (_, d) => {
      week = d.mtWeek || null;
      tab = 'week';
      void render('mis-tareas', { quiet: true });
    },
    mtNew: (t) => {
      void act(t, 'Creando la tarea…', async () => {
        const body = readNewTask('mt');
        await api('POST', '/v1/tasks', body);
        await render('mis-tareas', { quiet: true });
        return 'Tarea creada.';
      });
    },
  },
  change: {
    'mt-person': (c) => {
      who = c.value;
      void render('mis-tareas', { quiet: true });
    },
  },
};
